import _ from "lodash";
import React, { useState } from "react";
import { connect } from "react-redux";
import classes from "./SubmissionItem.scss";
import { CreateDropDown } from "Attachment/index";
import classNames from "classnames";
import { TextAreaInput, TextInput } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import ProjectsAttachmentList from "Projects/components/ProjectsAttachmentList";
import UseTemplateMediaCard from "Projects/components/UseTemplateMediaCard";
import {
  submissionItemDefaultProps,
  getSubmissionAttachmentExtraIconButtons,
} from "Projects/modules/ProjectModules";
import { isTextInputFieldType } from "Projects/modules/Utils";
import { openLinkInNewTab } from "modules/Services";
import { isIncreasedUploadLimitEnabled } from "Post/utils";
import { getSelectedCurriculumId } from "modules/CommonSelectors";

const styles = {
  createButtonComponentStyle: {
    icon: "AddOutlined",
    size: "medium",
    type: "ICON_BUTTON",
    variant: "neutral",
    label: "common:add_a_file",
  },
  templateContainer: {
    width: "100%",
  },
};

const SubmissionItem = ({
  submissionItem,
  activeAttachment,
  onAttachmentClick,
  onDeleteAttachment,
  onCreateAttachment,
  onUpdateAttachment,
  onUpdateInputField,
  showResponseText,
  isSubmissionLocked,
  disabled,
  titleCustomClass,
  dontShowAttachments,
  projectId,
  submissionTemplates,
  showUseTemplate,
  onUseTemplateClick,

  // dropdown and fieldConfig props
  title: submissionItemTitle,
  dropDownOptions,
  showSimilarity,

  isAnnotationEnabled,
  multiselect,
  canAddMaximumAttachments,
  defaultExportType,
  attachmentExportOptions,
  accept,
  maxSize,
  excludeFileExtensionsList,

  // for non attachment fields
  fieldType,
  placeholder: fieldPlaceholder,
  autoCheckSimilarityRef,
  containerStyle,
  showEditResponseButton,
  onEditAttachmentClick,
  showPdfTronEditButton,
  togglePdfTronEditButton,
  openLinkInNewTab,
  isCustomSubmissionType,
  isIncreasedUploadLimit,
  testId,
}) => {
  const {
    attachments,
    id: projectSubmissionId,
    submissionItem: { fieldUID },
  } = submissionItem;

  const { t } = useI18n(["common", "project"]);

  const isEditMode = !isSubmissionLocked && !disabled;

  const onClickOpenInNewTab = ({ e, attachment }) => {
    const attachmentUrl = _.get(attachment, "url", "");
    e.stopPropagation();
    if (attachmentUrl) openLinkInNewTab({ url: attachmentUrl });
  };

  const getExtraButtonsForAttachments = ({ attachment }) => {
    return getSubmissionAttachmentExtraIconButtons({
      attachment,
      t,
      onClickOpenInNewTab,
    });
  };
  const TextInputComponent = ({ isTextInput }) => {
    const { value: submissionFieldValue } = submissionItem;
    const [localFieldValues, setLocalFieldValues] = useState({
      [fieldUID]: submissionFieldValue,
    });

    const onUpdateLocalFieldValues = value => {
      setLocalFieldValues({
        [fieldUID]: value,
      });

      onUpdateInputField({
        submissionResponseId: projectSubmissionId,
        value,
      });
    };

    const value = localFieldValues[fieldUID];

    return isEditMode && !showResponseText ? (
      <div className={classes.textInputContainer}>
        {isTextInput ? (
          <TextAreaInput
            rows={1}
            maxRows={2}
            value={value}
            onChange={e => {
              onUpdateLocalFieldValues(e.target.value);
            }}
            testId={testId}
          />
        ) : (
          <TextInput
            width={"100%"}
            fullWidth={true}
            dsVersion="2.0"
            value={value}
            onChange={e => {
              onUpdateLocalFieldValues(e.target.value);
            }}
            placeholder={fieldPlaceholder}
            type={"number"}
            testId={testId}
          />
        )}
      </div>
    ) : (
      <div
        className={classNames("text-body", {
          [classes.subtleText]: !value,
        })}
      >
        {value ? value : t("project:no_response_text_added")}
      </div>
    );
  };

  const isInputField = isTextInputFieldType({ fieldType });
  const isTextInput = _.includes(["InputField", "TextArea"], fieldType);

  const areNoAttachments = attachments ? _.size(attachments) == 0 : true;

  const showTexInput = isInputField || showResponseText;

  const getSubmissionTemplateComponent = () => {
    if (isEditMode && showUseTemplate && _.size(submissionTemplates) > 0) {
      return _.map(submissionTemplates, template => {
        return (
          <UseTemplateMediaCard
            selectedProjectReportTemplate={template}
            onUseTemplateClick={() => {
              onUseTemplateClick({
                templateDetails: template,
              });
            }}
            containerStyle={styles.templateContainer}
          />
        );
      });
    }
  };

  const handleAttachmentClick = item => {
    if (onAttachmentClick) {
      onAttachmentClick({ attachmentId: item?.id, isAnnotationEnabled });
    }
  };

  return (
    <div className={classes.container} style={containerStyle}>
      {submissionItemTitle && (
        <div
          className={classNames({
            [titleCustomClass]: titleCustomClass,
            ["text-label"]: !titleCustomClass,
          })}
        >
          {t(submissionItemTitle)}
        </div>
      )}

      {showTexInput ? <TextInputComponent isTextInput={isTextInput} /> : null}
      {getSubmissionTemplateComponent()}
      {
        /** make this better */
        !isEditMode &&
        areNoAttachments &&
        !showTexInput &&
        !dontShowAttachments ? (
          <div className={classNames("text-body", classes.subtleText)}>
            {t("project:no_files_added")}
          </div>
        ) : (
          !areNoAttachments && (
            <ProjectsAttachmentList
              value={attachments}
              showCreateDropDown={false}
              shouldCallMutation={false}
              mode={isEditMode ? "edit" : "view"}
              createDropdownComponent={<span></span>}
              onClickAttachmentCard={
                onAttachmentClick ? handleAttachmentClick : undefined
              }
              onDeleteAttachmentCallback={({ attachmentId }) => {
                onDeleteAttachment({
                  attachmentIds: [attachmentId],
                });
              }}
              renameAttachmentCallBack={params => {
                onUpdateAttachment(params);
              }}
              activeAttachment={activeAttachment}
              alwaysShowActions={true}
              showDeleteModal={true}
              showAttachmentInfo={true}
              projectId={projectId}
              showSimilarity={showSimilarity}
              autoCheckSimilarityRef={autoCheckSimilarityRef}
              showEditResponseButton={showEditResponseButton}
              onEditAttachmentClick={onEditAttachmentClick}
              showPdfTronEditButton={showPdfTronEditButton}
              togglePdfTronEditButton={togglePdfTronEditButton}
              showAttachmentExtraIconButtons={true}
              getAttachmentExtraIconButtons={getExtraButtonsForAttachments}
            />
          )
        )
      }
      {isEditMode &&
      !isInputField &&
      !dontShowAttachments &&
      (!canAddMaximumAttachments ||
        (canAddMaximumAttachments &&
          _.size(attachments) < canAddMaximumAttachments)) ? (
        <div>
          <CreateDropDown
            multiple={true}
            isNewDropdown={true}
            parentId={projectSubmissionId}
            parentType="PROJECT_SUBMISSION_ITEM_RESPONSE"
            addAttachment={attachments => {
              onCreateAttachment({
                attachments,
                parentId: projectSubmissionId,
              });
            }}
            dsVersion="2.0"
            createButtonComponentConfig={styles.createButtonComponentStyle}
            dropDownOptions={dropDownOptions}
            multiselect={multiselect}
            defaultExportType={defaultExportType}
            attachmentExportOptions={attachmentExportOptions}
            accept={isCustomSubmissionType ? null : accept}
            maxSize={isCustomSubmissionType ? null : maxSize}
            excludeFileExtensionsList={excludeFileExtensionsList}
            useIconWithDropdownOptions={true}
            isIncreasedUploadLimit={isIncreasedUploadLimit}
            testId={testId}
          />
        </div>
      ) : null}
    </div>
  );
};

const mapActionCreators = {
  openLinkInNewTab,
};

const mapStateToProps = state => {
  const curriculumProgramId = getSelectedCurriculumId(state);
  const isIncreasedUploadLimit = isIncreasedUploadLimitEnabled(
    curriculumProgramId
  );

  return {
    isIncreasedUploadLimit,
  };
};

export default connect(mapStateToProps, mapActionCreators)(SubmissionItem);

SubmissionItem.defaultProps = submissionItemDefaultProps;
